import React, { useId } from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

import { panelHeading } from '../panelHeading';

export type SubPanelTone = 'plain' | 'positive' | 'negative';

export interface ISubPanelProps {
  /** Rendered uppercase by the label type role; pass the copy in its natural case. */
  title?: string;
  tone?: SubPanelTone;
  children: React.ReactNode;
  className?: string;
}

const TONE_BACKGROUNDS: Record<SubPanelTone, string> = {
  plain: tokens.color['surface-subpanel'],
  positive: tokens.color['tint-positive-bg'],
  negative: tokens.color['tint-negative-bg']
};

const SubPanelWrapper = styled.section<{ $tone: SubPanelTone }>`
  display: flex;
  flex-direction: column;
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.sm};
  background: ${({ $tone }) => TONE_BACKGROUNDS[$tone]};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

// h4, one level under the h3 card title this panel is always nested inside, so the inset block does
// not read as a sibling of its own container in the document outline.
const Title = styled.h4`
  ${panelHeading}
`;

const Body = styled.div`
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const SubPanel: React.FC<ISubPanelProps> = ({ title, tone = 'plain', children, className }) => {
  const titleId = useId();

  return (
    <SubPanelWrapper
      className={className}
      $tone={tone}
      // A bare `<section>` is not a region in the accessibility tree: it is exposed as one only once
      // it carries an accessible name, which is what makes the grouping this panel draws navigable.
      // An untitled panel stays a plain grouping rather than an unnamed region.
      aria-labelledby={title ? titleId : undefined}
      data-testid="sub-panel"
      data-tone={tone}
    >
      {title ? (
        <Title id={titleId} data-testid="sub-panel-title">
          {title}
        </Title>
      ) : null}
      <Body data-testid="sub-panel-body">{children}</Body>
    </SubPanelWrapper>
  );
};

export default SubPanel;
